"use client";

import classNames from 'classnames';
import { useMemo, useState } from "react";
import Button from "src/components/button/Button";
import { useMovieActivityContext } from "src/components/film/filmwebid/MovieActivityContextTypes";
import LoadingIndicator from "src/components/loadingindicator/LoadingIndicator";
import SpeechBubbleSvg from "src/components/svg/SpeechBubbleSvg";
import { useUserContext } from "src/lib/contexts/ContextTypes";
import type IMovieReview from "src/lib/filmwebid/IMovieReview";
import useFilmReviewList from "src/lib/filmwebid/useFilmReviewList";
import type { IFilmwebIdMovieIdentifier } from "src/lib/filmwebid/useMovieIdentifier";
import styles from './UserReviews.module.css';
import { locale } from "@locale";
import dynamic from "next/dynamic";
import { formatLocaleString } from "src/lib/locale/helpers";
const ReviewPanel = dynamic(() => import('src/components/filmwebid/ReviewPanel'), {
  loading: () => <p>{locale.progress.loading}</p>,
  ssr: false
});
const ReviewList = dynamic(() => import('src/components/film/filmwebid/ReviewList'), {
  loading: () => <p>{locale.progress.loading}</p>,
  ssr: false
});

//#region [Props]
type UserReviewsProps = {
  movieIdentifier: IFilmwebIdMovieIdentifier | null | undefined;
  title: string;
  maxRatingValue: number;
};
//#endregion

//#region [Component]
const UserReviews = ({
  movieIdentifier,
  title,
  maxRatingValue
}: UserReviewsProps) => {
  const movieActivityContext = useMovieActivityContext();
  const userContext = useUserContext();
  const [_reviewList, totalReviews, isFetchingList, fetchNext] = useFilmReviewList(movieIdentifier);
  // "show all" belongs to the film it was opened for, so another film starts closed again
  const filmKey = movieIdentifier ? `${movieIdentifier.type}:${movieIdentifier.value}` : "";
  const [showAllForFilm, setShowAllForFilm] = useState<string | null>(null);
  const showAll = showAllForFilm === filmKey;
  const [showReviewPanel, setShowReviewPanel] = useState(false);
  const reviewList: IMovieReview[] | undefined = useMemo(() => {
    if (!isFetchingList) {
      if (movieActivityContext.activity?.movieUserInteraction?.review) {
        const serverReview = _reviewList?.find(sr => sr.reviewId === movieActivityContext.activity?.movieUserInteraction?.reviewId);
        if (serverReview) {
          if (serverReview.reviewText !== movieActivityContext.activity.movieUserInteraction.review) {
            return [{
              ...serverReview,
              reviewText: movieActivityContext.activity.movieUserInteraction.review
            }, ..._reviewList.filter(r => r.reviewId !== serverReview.reviewId)];
          }
        } else {
          const mr: IMovieReview = {
            reviewId: movieActivityContext.activity.movieUserInteraction.reviewId!,
            reviewText: movieActivityContext.activity.movieUserInteraction.review,
            rating: movieActivityContext.activity.movieUserInteraction.rating,
            userName: userContext.userProfile!.userName!,
            hasUploadedPicture: !!userContext.userProfile?.picture,
            pictureUrl: userContext.userProfile?.picture,
            userId: userContext.userProfile!.userId!
          };
          return [mr, ...(_reviewList ?? [])];
        }
      } else {
        const userId = userContext.getUserId();
        return _reviewList?.filter(sr => sr.userId !== userId) ?? [];
      }
    }
    return _reviewList;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_reviewList, isFetchingList, movieActivityContext.activity?.movieUserInteraction?.review, movieActivityContext.activity?.movieUserInteraction?.reviewId, movieActivityContext.activity?.movieUserInteraction?.rating]);
  const _showReviewPanel = () => {
    if (userContext.isAuthenticated) {
      setShowReviewPanel(true);
    } else {
      userContext.doLogin();
    }
  };
  const onReviewPanelClose = (doUpdate: boolean) => {
    setShowReviewPanel(false);
    if (doUpdate) {
      movieActivityContext.activityUpdater();
    }
  };
  return <section id="userReviews" className={styles.SUserReviewsContainer}>
        <h2 className={styles.SHeading}>{locale.review.heading}</h2>

        <div className={styles.SReviews} aria-busy={isFetchingList || movieActivityContext.isLoading}>
            <ReviewList reviewList={reviewList} movieIdentifier={movieIdentifier} setShowReviewPanel={setShowReviewPanel} showAll={showAll} maxRatingValue={maxRatingValue} />
            {(reviewList?.length ?? 0) === 0 && <div className={styles.SNoReviews}>{formatLocaleString(locale.review.noReviews, {
          title: title
        })}</div>}
            {(isFetchingList || movieActivityContext.isLoading) && <LoadingIndicator position="InlineCenter" />}
        </div>

        {!isFetchingList && <div className={styles.SActions}>
            {(reviewList?.length ?? 0) > 2 && !showAll && <Button className={classNames(styles.SSeeMoreButton, {
        [styles['show-mobile']]: (reviewList?.length ?? 0) > 2,
        [styles['show-desktop']]: (reviewList?.length ?? 0) >= 6
      })} disabled={movieActivityContext.isLoading} text={locale.review.seeMore} onClick={() => {
        setShowAllForFilm(filmKey);
        fetchNext(totalReviews);
      }} dkVariant="darkOutline" />}
        </div>}
        <Button className={styles.SWriteReviewButton} disabled={movieActivityContext.isLoading} onClick={_showReviewPanel} text={locale.review.callToAction} icon={<SpeechBubbleSvg selected={!!movieActivityContext.activity?.movieUserInteraction?.review} />} dkVariant="dark" />
        {showReviewPanel && <ReviewPanel onClose={onReviewPanelClose} movieIdentifier={movieIdentifier} reviewId={movieActivityContext?.activity?.movieUserInteraction?.reviewId} userRating={movieActivityContext?.activity?.movieUserInteraction?.rating} userReview={movieActivityContext?.activity?.movieUserInteraction?.review} maxUserRatingValue={maxRatingValue} />}
    </section>;
};
export default UserReviews;
//#endregion